import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Hook for debounced email availability checking.
 *
 * @param {Function} checkFn - Service function: (email, excludeId?) => Promise<{ exists: boolean }>
 * @param {object} options
 * @param {number}  [options.debounce=500] - Debounce delay in ms
 * @param {string}  [options.excludeId]    - ID to exclude (for edit forms)
 * @returns {{ checkEmail, checking, available, error }}
 */
export default function useEmailCheck(checkFn, { debounce = 500, excludeId } = {}) {
  const [checking, setChecking] = useState(false);
  const [available, setAvailable] = useState(null); // null = not checked, true = available, false = taken
  const [error, setError] = useState('');
  const timerRef = useRef(null);
  const abortRef = useRef(null);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const checkEmail = useCallback(
    (email) => {
      // Reset state
      setAvailable(null);
      setError('');

      if (timerRef.current) clearTimeout(timerRef.current);

      // Skip if empty or invalid format
      if (!email || !EMAIL_REGEX.test(email)) {
        setChecking(false);
        return;
      }

      setChecking(true);

      // Increment abort counter so stale responses are ignored
      const callId = Date.now();
      abortRef.current = callId;

      timerRef.current = setTimeout(async () => {
        try {
          const response = await checkFn(email, excludeId);
          // Ignore if a newer call has been made
          if (abortRef.current !== callId) return;

          if (response.exists) {
            setAvailable(false);
            setError('This email is already in use');
          } else {
            setAvailable(true);
            setError('');
          }
        } catch {
          if (abortRef.current !== callId) return;
          // Don't block the form on network errors
          setAvailable(null);
          setError('');
        } finally {
          if (abortRef.current === callId) {
            setChecking(false);
          }
        }
      }, debounce);
    },
    [checkFn, debounce, excludeId],
  );

  const reset = useCallback(() => {
    setChecking(false);
    setAvailable(null);
    setError('');
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  return { checkEmail, checking, available, error, reset };
}
